import { Button as DSButton } from '@hivebrite/design-system/Button';
import { ButtonInLink as DSButtonInLink } from '@hivebrite/design-system/ButtonInLink';
import type { Theme } from '@hivebrite/design-system/Theme';

import { hasLink } from 'hb-builder/utils/attributes';
import { getIsExternalLink, getLink } from 'hb-builder/utils/links';
import {
  BUTTON_MODE_OPTIONS,
  type ButtonItemAttributes,
} from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';

export const BUTTON_SIZE_DEFAULT_VALUE = 'normal';

interface ButtonUIProps {
  attributes?: ButtonItemAttributes;
  children?: string;
  dataTestId?: string;
  defaultFontFamily?: string;
  theme: Theme;
}

const ButtonUI = ({
  attributes,
  children,
  dataTestId,
  defaultFontFamily,
  theme,
}: ButtonUIProps) => {
  if (!attributes) {
    return null;
  }

  const ButtonUIComponent = hasLink({ link: attributes?.buttonLink })
    ? DSButtonInLink
    : DSButton;

  const domain = window.location.hostname;

  const linkProps = attributes?.buttonLink && {
    forceDefaultLink: getIsExternalLink(attributes.buttonLink, domain),
    target: attributes.buttonLinkNewPage ? '_blank' : '_self',
    to: getLink(attributes.buttonLink, domain),
  };

  return attributes.mode === BUTTON_MODE_OPTIONS.NETWORK ? (
    <ButtonUIComponent
      dataTestId={dataTestId}
      key="button-network"
      variant={attributes.variant}
      // attributes.size only exists on the v1 of the button rework project
      // we're keeping it here with a fallback to BUTTON_SIZE_DEFAULT_VALUE to avoid breaking existing buttons
      size={attributes.size || BUTTON_SIZE_DEFAULT_VALUE}
      truncate={attributes?.truncateText ?? true}
      {...(attributes.buttonLink && { linkProps })}
    >
      {children}
    </ButtonUIComponent>
  ) : (
    <ButtonUIComponent
      dataTestId={dataTestId}
      key="button-custom"
      variant={attributes.inversed ? 'inversed' : attributes.variant}
      builderProps={{
        builderBackgroundColor: attributes.buttonBackgroundColor,
        builderBorderRadius:
          attributes.buttonRadius || theme.themeVariables.globalBorderRadius,
        builderFontFamily: attributes.buttonFontFamily || defaultFontFamily,
        builderFontSize: attributes.buttonFontSize,
        builderLetterSpacing: attributes.buttonLetterSpacing,
        builderPadding: attributes.buttonPadding || '7px 12px',
        builderTextColor: attributes.buttonColor,
        ...(attributes.hasBorder
          ? {
              builderBorderColor: attributes.borderColor,
              builderBorderWidth: attributes.borderWidth,
            }
          : {}),
      }}
      truncate={attributes?.truncateText ?? true}
      {...(attributes.buttonLink && { linkProps })}
    >
      {children}
    </ButtonUIComponent>
  );
};

export default ButtonUI;
